import { ColumnsType } from 'rc-table/lib/interface';
import { Button, ButtonProps, Container, Toggle } from '@/components/common';
import { PageBanner } from '@/components/common/PageBanner';
import { DataTableType, Table, TableCell, TableHeadCell } from '@/components/common/Table';
import {
  useGetAccessKeys,
  useCreateAccessKeys,
  useDownloadAccessKeys,
  useRevokeAccessKeys,
  AccessKeysResponse,
} from '@/api/queryHooks/useMerchantDashboardController';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { downloadJson, formatDate } from '@/utils/functions';
import { useCheckCapabilities } from '@/api/queryHooks/useMerchantCryptoController';
import styles from './ApiToken.module.css';

const banners = [
  {
    id: 1,
    title: 'Is your e-commerce store built on Wordpress?',
    description: 'Check out our Wordpress plug-in and start accepting cryptocurrency today',
    buttonLabel: 'Learn More',
    gradientSrc: '/assets/OverviewGradient.svg',
    variants: 'whiteCreator',
    href: 'https://wordpress.com/plugins/relaypay',
  },
  {
    id: 2,
    title: 'Is your e-commerce store built on Magento?',
    description: 'Check out our Magento plug-in and start accepting cryptocurrency today',
    buttonLabel: 'Learn More',
    gradientSrc: '/assets/TransactionsBusinessGradient.svg',
    href: 'https://marketplace.magento.com/relaypay-payment.html',
  },
];

function buildTableData(data?: AccessKeysResponse) {
  return [
    {
      key: 1,
      publicKey: data?.publicKey,
      date: formatDate(data?.created),
      status: data?.active || false,
      actions: 'Download',
    },
  ];
}

export const ApiTokenPage = () => {
  const { refetch: checkCapabilities } = useCheckCapabilities([{}, { enabled: false }]);
  const { data: getAccessKeysData, refetch: getAccessKeys } = useGetAccessKeys();
  const { mutate: createAccessKeys, isLoading: isCreateAccessKeysLoading } = useCreateAccessKeys({
    onSuccess() {
      checkCapabilities();
      getAccessKeys();
    },
  });
  const { refetch: downloadAccessKeys } = useDownloadAccessKeys({
    enabled: false,
    onSuccess(data) {
      downloadJson(data);
    },
  });

  const { mutate: revokeAccessKeysMutation } = useRevokeAccessKeys({
    onSuccess() {
      getAccessKeys();
    },
  });
  const { isDesktop } = useMediaQuery();
  const isKeysActive = getAccessKeysData?.active || false;

  function revokeAccessKeys(isSelected: boolean) {
    if (!isSelected) {
      revokeAccessKeysMutation();
    }
  }

  const columns: ColumnsType<DataTableType> = [
    {
      title: <TableHeadCell sort={false}>Public key</TableHeadCell>,
      align: 'left',
      dataIndex: 'publicKey',
      render: (value) => <TableCell isBold>{value}</TableCell>,
      key: 'publicKey',
    },
    {
      title: <TableHeadCell sort={false}>Date</TableHeadCell>,
      align: 'left',
      dataIndex: 'date',
      render: (value) => <TableCell>{value}</TableCell>,
      key: 'date',
    },
    {
      title: <TableHeadCell sort={false}>Status</TableHeadCell>,
      align: 'right',
      dataIndex: 'status',
      render: (value) => (
        <TableCell>
          <Toggle size={isDesktop ? 'big' : 'small'} isSelected={value} onChange={revokeAccessKeys} />
        </TableCell>
      ),
      key: 'status',
    },
    {
      title: <TableHeadCell sort={false}>Actions</TableHeadCell>,
      align: 'right',
      dataIndex: 'actions',
      render: (value) => (
        <TableCell>
          <Button
            variants="subtle"
            iconPosition="right"
            icon="download"
            size={isDesktop ? 'mediumSubtle' : 'smallSubtle'}
            onPress={() => downloadAccessKeys()}
            className={styles.downloadButton}
          >
            {value}
          </Button>
        </TableCell>
      ),
      key: 'actions',
    },
  ];

  return (
    <Container className={styles.container}>
      <div className={styles.bannerWrapper}>
        {banners.map(({ id, ...banner }) => {
          return <PageBanner key={id} {...banner} variants={banner.variants as ButtonProps['variants']} />;
        })}
      </div>
      <div className={styles.head}>
        <div>
          <div className={styles.title}>API Tokens</div>
          <div className={styles.description}>
            Add new, revoke, and change RelayPay API access tokens for your RelayPay account.
          </div>
        </div>
        <Button
          additionalBg="blue"
          variants="secondary"
          size={isDesktop ? 'medium' : 'small'}
          onPress={() => createAccessKeys()}
          loading={isCreateAccessKeysLoading}
        >
          Generate New Token
        </Button>
      </div>
      <div className={styles.table}>
        <Table columns={columns} data={isKeysActive ? buildTableData(getAccessKeysData) : []} typeOfData="tokens" />
      </div>
    </Container>
  );
};
